// Burn-up series for the Booked Contribution card: the REAL cumulative
// booked-contribution series (period start → today for the current period, or
// the full period when viewing a past month/quarter) plus a straight pace
// line to target. Headless — the shell renders it. The cumulative shape is
// authoritative daily data from GET /views/dashboard/monthly-contribution;
// only the pace line (a straight target → period-end reference) is derived
// here, which is legitimately a presentation concern.
//
// ``day`` is a 1-based sequential index within the *viewed period*
// (`period_start`..`period_end`, both backend-computed so the frontend never
// re-derives fiscal-quarter boundaries) — not literally "day of calendar
// month". This is what lets the same chart plot either a ~30-day month or a
// ~90-day fiscal quarter unchanged.

import type { MonthlyContribution } from "../data/api-types";

export interface BurnUpPoint {
  day: number;
  cumulative: number | null; // realised booked contribution to date; null after today
  pace: number | null; // straight line 0 → target across the period; null if no target
}

export interface ContributionBurnUp {
  points: BurnUpPoint[];
  today: number;
  daysInMonth: number; // total days in the viewed period (name kept for the chart's existing prop)
  workingDayOfMonth: number; // working days elapsed including today (from backend)
  workingDaysInMonth: number; // from backend — bank holidays included
}

function daysBetweenInclusive(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
}

export function buildContributionBurnUp(
  contribution: MonthlyContribution,
): ContributionBurnUp {
  const target = contribution.target ?? 0;
  const daily = contribution.daily ?? [];
  const periodStart = contribution.period_start;
  const daysInPeriod = daysBetweenInclusive(
    contribution.period_start,
    contribution.period_end,
  );

  // The backend series is authoritative for realised cumulative — one entry
  // per calendar day from the period's start, clipped to today only for the
  // current period. Map each ISO date to its sequential index within the
  // period (avoids re-deriving month/quarter boundaries in JS).
  const cumulativeByIndex = new Map<number, number>();
  for (const point of daily) {
    const index = daysBetweenInclusive(periodStart, point.date);
    cumulativeByIndex.set(index, point.cumulative_contribution_amt ?? 0);
  }
  // "Today" on this axis = the last index the backend included.
  const today =
    daily.length > 0
      ? daysBetweenInclusive(periodStart, daily[daily.length - 1]!.date)
      : 1;

  const points: BurnUpPoint[] = [];
  for (let day = 1; day <= daysInPeriod; day++) {
    // Realised cumulative only up to today (pass the backend value straight
    // through); null afterwards so the filled area stops at today.
    const cumulative =
      day <= today ? (cumulativeByIndex.get(day) ?? null) : null;
    // Straight pace line 0 → target across the period; null when no target set.
    const pace = target > 0 ? Math.round((target * day) / daysInPeriod) : null;
    points.push({ day, cumulative, pace });
  }

  return {
    points,
    today,
    daysInMonth: daysInPeriod,
    workingDayOfMonth: contribution.working_day_of_month ?? 0,
    workingDaysInMonth: contribution.working_days_in_month ?? 0,
  };
}
